import { useCallback, useState } from 'react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type {
  CycleAnalysisResult,
  CreateInvoiceGroup,
  DeepAccountDetail,
  CycleAccountsData,
  EmptyDraftInvoicesResult,
  InvoiceCycle,
  StuckHeldTimesheetsResult,
} from '../types';

export function useCycles() {
  const [cycles, setCycles] = useState<InvoiceCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosRubyClient.get('/api/invoice_health/cycles');
      setCycles(res.data.data.cycles || []);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load cycles',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return { cycles, loading, error, fetch };
}

export function useCycleAccounts() {
  const [data, setData] = useState<CycleAccountsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (params: {
      from: string;
      to: string;
      referenceTime: string;
      futurePreview?: boolean;
    }) => {
      setLoading(true);
      setError(null);

      try {
        const qs = new URLSearchParams();
        qs.append('from', params.from);
        qs.append('to', params.to);
        qs.append('reference_time', params.referenceTime);
        if (params.futurePreview) {
          qs.append('future_preview', 'true');
        }

        const res = await axiosRubyClient.get(
          `/api/invoice_health/cycle_accounts?${qs.toString()}`,
        );
        setData(res.data.data);
      } catch (err: any) {
        setError(
          err.response?.data?.errors?.join(', ') ||
            err.message ||
            'Failed to load cycle accounts',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, fetch, clear };
}

export function useCycleAnalysis() {
  const [analysis, setAnalysis] = useState<CycleAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (params: {
      from: string;
      to: string;
      referenceTime: string;
      futurePreview?: boolean;
    }): Promise<{
      success: boolean;
      data?: CycleAnalysisResult;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setAnalysis(null);

      try {
        const qs = new URLSearchParams();
        qs.append('from', params.from);
        qs.append('to', params.to);
        qs.append('reference_time', params.referenceTime);
        if (params.futurePreview) {
          qs.append('future_preview', 'true');
        }

        const res = await axiosRubyClient.get(
          `/api/invoice_health/cycle_analysis?${qs.toString()}`,
        );
        const data = res.data.data as CycleAnalysisResult;
        setAnalysis(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to run invoice analysis';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  return { analysis, loading, error, fetch, clear };
}

export function useStuckHeldTimesheets() {
  const [report, setReport] = useState<StuckHeldTimesheetsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const res = await axiosRubyClient.get(
        '/api/invoice_health/stuck_held_timesheets',
      );
      const data = res.data.data as StuckHeldTimesheetsResult;
      setReport(data);
      return { success: true, data };
    } catch (err: any) {
      const message =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load stuck held timesheets';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setReport(null);
    setError(null);
  }, []);

  return { report, loading, error, fetch, clear };
}

export function useResolveStuckHeldTimesheet() {
  const [loadingTimesheetId, setLoadingTimesheetId] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(
    async (params: {
      timesheetId: number;
      action: 'clear_hold' | 'restore_line_item';
    }): Promise<{ success: boolean; error?: string }> => {
      setLoadingTimesheetId(params.timesheetId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/timesheets/${params.timesheetId}/resolve_stuck_hold`,
          { resolution_action: params.action },
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to resolve stuck held timesheet';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingTimesheetId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingTimesheetId, error, resolve, clearError };
}

export function useEmptyDraftInvoices() {
  const [report, setReport] = useState<EmptyDraftInvoicesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const res = await axiosRubyClient.get(
        '/api/invoice_health/empty_draft_invoices',
      );
      const data = res.data.data as EmptyDraftInvoicesResult;
      setReport(data);
      return { success: true, data };
    } catch (err: any) {
      const message =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load empty draft invoices';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setReport(null);
    setError(null);
  }, []);

  return { report, loading, error, fetch, clear };
}

export function useSoftDeleteEmptyDraftInvoice() {
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(
    async (
      invoiceId: number,
    ): Promise<{ success: boolean; error?: string }> => {
      setLoadingInvoiceId(invoiceId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/invoices/${invoiceId}/soft_delete_empty_draft`,
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to soft delete empty draft invoice';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingInvoiceId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingInvoiceId, error, remove, clearError };
}

export function useRemoveLineItem() {
  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (invoiceItemId: number) => {
    setLoadingItemId(invoiceItemId);
    setError(null);

    try {
      await axiosRubyClient.delete(
        `/api/invoice_health/invoice_items/${invoiceItemId}`,
      );
      return { success: true };
    } catch (err: any) {
      const message =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to remove line item';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoadingItemId(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingItemId, error, remove, clearError };
}

export type ReconcileAction = {
  invoice_id: number;
  account_id: number;
  customer_id: number;
  stripe_invoice_id: string;
  action: string;
  details: Record<string, unknown>;
};

export type ReconcilePreview = {
  mode: 'dry_run' | 'execute';
  invoices_scanned: number;
  actions: ReconcileAction[];
  stats: Record<string, number>;
};

export function useReconcile() {
  const [preview, setPreview] = useState<ReconcilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = useCallback(
    async (
      accountId: number,
      cycleParams?: { from: string; to: string; referenceTime: string },
    ): Promise<{
      success: boolean;
      data?: ReconcilePreview;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setPreview(null);

      try {
        const qs = new URLSearchParams();
        if (cycleParams) {
          qs.append('from', cycleParams.from);
          qs.append('to', cycleParams.to);
          qs.append('reference_time', cycleParams.referenceTime);
        }
        const query = qs.toString();
        const url = `/api/invoice_health/accounts/${accountId}/reconcile${query ? `?${query}` : ''}`;
        const res = await axiosRubyClient.get(url);
        const data = res.data.data as ReconcilePreview;
        setPreview(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load reconcile preview';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const execute = useCallback(
    async (
      accountId: number,
      cycleParams?: { from: string; to: string; referenceTime: string },
    ): Promise<{
      success: boolean;
      data?: ReconcilePreview;
      error?: string;
    }> => {
      setExecuting(true);
      setError(null);

      try {
        const res = await axiosRubyClient.post(
          `/api/invoice_health/accounts/${accountId}/reconcile`,
          cycleParams
            ? {
                from: cycleParams.from,
                to: cycleParams.to,
                reference_time: cycleParams.referenceTime,
              }
            : {},
        );
        const data = res.data.data as ReconcilePreview;
        setPreview(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to execute reconciliation';
        setError(message);
        return { success: false, error: message };
      } finally {
        setExecuting(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  return { preview, loading, executing, error, fetchPreview, execute, clear };
}

export function useCreateInvoice() {
  const [loadingAccountId, setLoadingAccountId] = useState<number | null>(null);
  const [groups, setGroups] = useState<CreateInvoiceGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = useCallback(
    async (params: {
      accountId: number;
      referenceTime: string;
      from: string;
      to: string;
      futurePreview?: boolean;
    }): Promise<{
      success: boolean;
      data?: CreateInvoiceGroup[];
      error?: string;
    }> => {
      setLoadingAccountId(params.accountId);
      setError(null);
      setGroups([]);

      try {
        const qs = new URLSearchParams();
        qs.append('reference_time', params.referenceTime);
        qs.append('from', params.from);
        qs.append('to', params.to);
        if (params.futurePreview) {
          qs.append('future_preview', 'true');
        }

        const res = await axiosRubyClient.get(
          `/api/invoice_health/accounts/${params.accountId}/create_invoice?${qs.toString()}`,
        );
        const nextGroups = (res.data.data.groups || []) as CreateInvoiceGroup[];
        setGroups(nextGroups);
        return { success: true, data: nextGroups };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load invoice creation options';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingAccountId(null);
      }
    },
    [],
  );

  const create = useCallback(
    async (params: {
      accountId: number;
      referenceTime: string;
      from: string;
      to: string;
      futurePreview?: boolean;
      groupKeys: string[];
    }): Promise<{ success: boolean; error?: string }> => {
      setLoadingAccountId(params.accountId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/accounts/${params.accountId}/create_invoice`,
          {
            reference_time: params.referenceTime,
            from: params.from,
            to: params.to,
            future_preview: params.futurePreview,
            group_keys: params.groupKeys,
          },
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to create invoice';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingAccountId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
    setGroups([]);
  }, []);

  return { loadingAccountId, groups, error, fetchPreview, create, clearError };
}

export function useAssignTimesheets() {
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assign = useCallback(
    async (params: {
      invoiceId: number;
      timesheetIds: number[];
    }): Promise<{ success: boolean; error?: string }> => {
      setLoadingInvoiceId(params.invoiceId);
      setError(null);

      try {
        await axiosRubyClient.post(
          `/api/invoice_health/invoices/${params.invoiceId}/assign_timesheets`,
          { timesheet_ids: params.timesheetIds },
        );
        return { success: true };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to assign timesheets';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoadingInvoiceId(null);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingInvoiceId, error, assign, clearError };
}

export function useVoidInvoice() {
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const voidInvoice = useCallback(async (invoiceId: number) => {
    setLoadingInvoiceId(invoiceId);
    setError(null);

    try {
      await axiosRubyClient.post(
        `/api/invoice_health/invoices/${invoiceId}/void`,
      );
      return true;
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to void invoice',
      );
      return false;
    } finally {
      setLoadingInvoiceId(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loadingInvoiceId, error, voidInvoice, clearError };
}

export function useAccountDetail() {
  const [accountDetail, setAccountDetail] = useState<DeepAccountDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inspectAccount = useCallback(
    async (
      accountId: number,
      timesheetIds?: number[],
    ): Promise<{
      success: boolean;
      data?: DeepAccountDetail;
      error?: string;
    }> => {
      setLoading(true);
      setError(null);
      setAccountDetail(null);

      try {
        const qs = new URLSearchParams();
        if (timesheetIds && timesheetIds.length > 0) {
          qs.append('timesheet_ids', timesheetIds.join(','));
        }

        const query = qs.toString();
        const res = await axiosRubyClient.get(
          `/api/invoice_health/accounts/${accountId}${query ? `?${query}` : ''}`,
        );
        const data = res.data.data as DeepAccountDetail;
        setAccountDetail(data);
        return { success: true, data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load account detail';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setAccountDetail(null);
    setError(null);
  }, []);

  return {
    accountDetail,
    loading,
    error,
    inspectAccount,
    clear,
  };
}
